import React, { useMemo, useState } from "react";
import { Eyebrow, FadeUp } from "../components/site/Primitives";
import ProductCard from "../components/site/ProductCard";
import { PRODUCT_CATEGORIES } from "../lib/products";
import { useProducts } from "../hooks/useProducts";
import { Loader2 } from "lucide-react";

export default function ProductsPage() {
  const [active, setActive] = useState("All");
  const { products, loading, error } = useProducts();

  const filtered = useMemo(
    () => (active === "All" ? products : products.filter((p) => p.category === active)),
    [active, products]
  );

  return (
    <div className="bg-[#0A0A09]">
      <section className="relative pt-12 pb-16 lg:pb-20 kraft-texture">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <FadeUp>
            <Eyebrow>Catalogue</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="mt-8 font-grotesk text-5xl md:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.92] text-[#F7F5F0] max-w-5xl">
              Products built for{" "}
              <span className="text-[#E85D2F]">Africa</span>
              <span className="text-[#E85D2F]">.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#F7F5F0]/70 leading-relaxed font-light">
              Bowls, trays, bags and cutlery — every line is wholesale-ready,
              private-label-ready and designed to perform from kitchen to customer.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Filters */}
      <section className="border-y border-[#F7F5F0]/10 sticky top-[80px] z-40 bg-[#0A0A09]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-5 flex gap-2 overflow-x-auto">
          {PRODUCT_CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`filter-${cat.toLowerCase()}`}
              onClick={() => setActive(cat)}
              className={`px-5 py-2.5 rounded-full text-sm tracking-wide whitespace-nowrap transition-colors ${
                active === cat
                  ? "bg-[#E85D2F] text-[#0A0A09]"
                  : "border border-[#F7F5F0]/15 text-[#F7F5F0]/70 hover:text-[#F7F5F0] hover:border-[#E85D2F]/40"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[#F7F5F0]/60">
              <Loader2 className="h-6 w-6 animate-spin mr-3 text-[#E85D2F]" />
              Loading catalogue…
            </div>
          )}
          {error && !loading && (
            <p data-testid="products-error" className="text-center text-[#F47A4A] py-20">
              Could not load products. Please try again shortly.
            </p>
          )}
          {!loading && !error && filtered.length === 0 && (
            <p className="text-center text-[#F7F5F0]/60 py-20">
              Nothing in this category yet — get in touch for custom solutions.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filtered.map((p, i) => (
              <FadeUp key={p.id} delay={i * 0.06}>
                <ProductCard product={p} index={i} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
